// The columns a person adds to a work item's related-items tables
// (Subtasks, Child work items, Linked work items) with the "+" at the end of
// the table's header — chosen the way Zebra Search's Choose Columns chooses
// them, and remembered the same way: in this browser, per person.
//
// One set per SECTION per WORK TYPE of the item being viewed: a Project's
// Subtasks can show hours while an Assembly's show its due date, and its
// Linked work items something else again.
//
// The table's own columns — the work (icon, key, summary), status and the
// assignee — are always there, so they are not offered.

// The standard fields the picker offers, as Zebra Search's Choose Columns
// offers them, less the ones every related row already shows.
export const RELATED_STANDARD_FIELDS = [
  { fieldId: 'assignee', name: 'Assignee' },
  { fieldId: 'reporter', name: 'Reporter' },
  { fieldId: 'priority', name: 'Priority' },
  { fieldId: 'startDate', name: 'Start Date' },
  { fieldId: 'duedate', name: 'Due Date' },
  { fieldId: 'created', name: 'Created' },
  { fieldId: 'updated', name: 'Updated' },
  { fieldId: 'labels', name: 'Labels' },
  { fieldId: 'components', name: 'Components' },
  { fieldId: 'fixVersions', name: 'Fix Versions' },
  { fieldId: 'resolution', name: 'Resolution' },
  { fieldId: 'project', name: 'Project' },
  { fieldId: 'timeoriginalestimate', name: 'Original Estimate' },
  { fieldId: 'timeestimate', name: 'Remaining Estimate' },
  { fieldId: 'timespent', name: 'Time Spent' },
];

export const RELATED_SECTIONS = ['subtasks', 'children', 'links'];

const STORAGE_PREFIX = 'zebra_related_cols';
const COLUMN_WIDTH = 140;

export function relatedColumnsKey(section, workTypeId) {
  return `${STORAGE_PREFIX}:${section}:${workTypeId || 'any'}`;
}

// The field ids chosen for a section, in the order they were added. Storage
// that is blocked or holds something else reads as none chosen.
export function readRelatedColumnIds(section, workTypeId, storage = globalThis.localStorage) {
  try {
    const ids = JSON.parse(storage.getItem(relatedColumnsKey(section, workTypeId)) || '[]');
    return Array.isArray(ids) ? ids.filter((id) => typeof id === 'string' && id) : [];
  } catch {
    return [];
  }
}

export function writeRelatedColumnIds(section, workTypeId, ids, storage = globalThis.localStorage) {
  try {
    const key = relatedColumnsKey(section, workTypeId);
    if (ids.length) storage.setItem(key, JSON.stringify(ids));
    else storage.removeItem(key);
  } catch { /* storage blocked — the choice lasts until the page closes */ }
}

// Add a field, or take it away if it is already there.
export function toggleRelatedColumnId(ids, fieldId) {
  return ids.includes(fieldId) ? ids.filter((id) => id !== fieldId) : [...ids, fieldId];
}

// Everything the picker can offer: the standard fields, then every custom
// field the site has (ranking fields aside, as in Zebra Search), A-Z.
export function relatedFieldCatalogue(jiraFields) {
  const custom = (Array.isArray(jiraFields) ? jiraFields : [])
    .filter((f) => f.id?.startsWith('customfield_') && !f.id.includes('_rank'))
    .map((f) => ({ fieldId: f.id, name: f.name || f.id }))
    .sort((a, b) => a.name.localeCompare(b.name));
  return [...RELATED_STANDARD_FIELDS, ...custom];
}

// The chosen ids as the panel's columns ({ id, name, fieldId, type, width },
// the shape a Folder's columns have, so they draw and edit the same way). A
// custom field the site no longer has is dropped — once the field list has
// loaded; until then it shows under its id rather than jump in later.
export function relatedColumnsFromIds(ids, jiraFields) {
  const catalogue = relatedFieldCatalogue(jiraFields);
  const loaded = Array.isArray(jiraFields) && jiraFields.length > 0;
  return ids
    .map((fieldId) => {
      const known = catalogue.find((f) => f.fieldId === fieldId);
      if (!known && loaded) return null;
      return { id: `related-${fieldId}`, name: known?.name || fieldId, fieldId, type: 'field', width: COLUMN_WIDTH };
    })
    .filter(Boolean);
}
